<template>
  <div
    v-loading="isLoading"
    class="max-w-360 m-auto w-full h-full flex flex-col justify-center items-center"
  >
    <div class="flex-1 flex justify-center items-center">
      <div class="flex justify-center items-start mb-3 mt-1">
        <div class="relative inline-flex">
          <TargetKeyboardPanel
            :is-fn="currentLayer === 'fn'"
            :is-read-only="isMacFnReadOnly"
            :platform="currentPlatform"
            @update-setting="updateKeyMappingWithData"
          />

          <div class="absolute top-0 left-full flex flex-col gap-3 pl-4 pt-4">
            <Button
              v-for="layer in getLayer()"
              :key="layer.value"
              color="glass"
              :active="currentLayer === layer.value"
              @click="() => handleChangeLayer(layer.value)"
            >
              {{ layer.name }}
            </Button>
          </div>
        </div>
      </div>
    </div>
    <Tabs
      v-model="activeTabValue"
      class="shrink-0 mb-2 w-full h-82 min-h-fit flex flex-col items-center gap-4.5"
    >
      <TabsList class="p-0 flex items-center gap-4 h-10">
        <TabsTrigger v-for="tab in tabConfigs" :key="tab.value" :value="tab.value">
          <Button color="glass" :active="tab.value === activeTabValue" class="h-10">
            {{ $te(tab.label) ? $t(tab.label) : tab.label }}
          </Button>
        </TabsTrigger>
      </TabsList>

      <transition
        name="fade"
        mode="out-in"
        enter-active-class="animate-in fade-in duration-150"
        leave-active-class="animate-out fade-out duration-150"
      >
        <component :is="activeTabComponent" />
      </transition>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
  import {
    computed,
    defineAsyncComponent,
    markRaw,
    onMounted,
    onUnmounted,
    provide,
    ref,
    shallowRef,
    watch,
  } from 'vue'
  import { storeToRefs } from 'pinia'
  import { Button, Tabs, TabsList, TabsTrigger, toast } from '@rapoo/element'
  import TargetKeyboardPanel from './components/TargetKeyboardPanel.vue'
  import { useConfigurationStore, useDeviceStore, useLocaleStore, useUserStore } from '@/stores'
  import type { KeyFeaturesCapability, KeysCapability } from '@rapoo/device-sdk'
  import { useMyKeysConf } from './composables/useMyKeysConf'
  import { useKeyboardPlatform } from './composables/useKeyboardPlatform'
  import { useMacroRepository } from './components/TabMacro/composables/useMacroRepository'
  import { createCoalesceLatestRequestQueue } from '@/utils/coalesceLatestRequest'
  import { buildFactoryDefaultSetting, buildKeySettingFromPayload } from './utils/keyMapSettings'
  import { pickFactoryKeyMap, resolveFactoryKey } from './utils/factoryKeys'
  import { findSettingIndexByTargetKey, isSameKeyIdentity, parseNumber } from './utils/keyIdentity'
  import type { KeySetting, KeyChangePayload } from './types'
  import {
    SelectedKey,
    SelectedKeySymbol,
    KeySettingsSymbol,
    RightSelectedKeySymbol,
    ActiveTabValueSymbol,
    KeyLayerSymbol,
    KeyPlatformSymbol,
    RefreshLayerKeySettingsSymbol,
    RestoreKeyMappingWithTargetSymbol,
    SuppressSelectedKeyUxSymbol,
    UpdateKeyMappingWithTargetSymbol,
  } from './types'
  import TabMacro from '@/views/device/keyboard/keys/components/TabMacro/index.vue'

  type KeyLayer = 'default' | 'fn'

  const { t } = useLocaleStore()
  const userStore = useUserStore()
  const { isAdmin } = storeToRefs(userStore)

  // 当前选中的层
  const currentLayer = ref<KeyLayer>('default')
  provide(KeyLayerSymbol, currentLayer)
  const { currentPlatform, refreshKeyboardPlatform } = useKeyboardPlatform()
  provide(KeyPlatformSymbol, currentPlatform)
  const platformReady = shallowRef(false)

  const isMacFnReadOnly = computed(
    () => currentPlatform.value === 'mac' && currentLayer.value === 'fn',
  )

  const tabComponentMap = {
    normal: markRaw(defineAsyncComponent(() => import('./components/TabNormal.vue'))),
    media: markRaw(defineAsyncComponent(() => import('./components/TabMedia.vue'))),
    config: markRaw(defineAsyncComponent(() => import('./components/TabConfig.vue'))),
    dks: markRaw(defineAsyncComponent(() => import('./components/TabDKS/index.vue'))),
    mt: markRaw(defineAsyncComponent(() => import('./components/TabMT.vue'))),
    tgl: markRaw(defineAsyncComponent(() => import('./components/TabTGL.vue'))),
    rs: markRaw(defineAsyncComponent(() => import('./components/TabRS.vue'))),
    socd: markRaw(defineAsyncComponent(() => import('./components/TabSOCD.vue'))),
    advanced: markRaw(defineAsyncComponent(() => import('./components/TabAdvanced/index.vue'))),
    macro: markRaw(TabMacro),
  } as const

  const allTabConfigs = computed(() => {
    const channel = import.meta.env.VITE_CHANNEL as string
    const tabs = [
      {
        label: 'keys.tabs.normal',
        value: 'normal',
        component: tabComponentMap.normal,
        show: true,
      },
      {
        label: 'keys.tabs.media',
        value: 'media',
        component: tabComponentMap.media,
        show: true,
      },
      {
        label: 'keys.tabs.config',
        value: 'config',
        component: tabComponentMap.config,
        // 网吧渠道管理员不显示配置选项
        show: !(isAdmin.value && channel === 'china_cybercafe'),
      },
      {
        label: 'DKS',
        value: 'dks',
        component: tabComponentMap.dks,
        show: true,
      },
      {
        label: 'MT',
        value: 'mt',
        component: tabComponentMap.mt,
        show: true,
      },
      {
        label: 'TGL',
        value: 'tgl',
        component: tabComponentMap.tgl,
        show: true,
      },
      {
        label: 'RS',
        value: 'rs',
        component: tabComponentMap.rs,
        show: true,
      },
      {
        label: 'SOCD',
        value: 'socd',
        component: tabComponentMap.socd,
        show: true,
      },
      {
        label: 'keys.tabs.advanced',
        value: 'advanced',
        component: tabComponentMap.advanced,
        show: true,
      },
      {
        label: 'keys.tabs.macro',
        value: 'macro',
        component: tabComponentMap.macro,
        show: true,
      },
    ]

    return tabs.filter((tab) => tab.show)
  })

  // 读取设备的高级功能能力配置，默认可选全部关闭
  const keyFeatures = computed(() => {
    const cap = currentDevice.value?.manifest.capabilities?.find(
      (c): c is KeyFeaturesCapability => c.type === 'keyFeatures',
    )
    return (
      cap?.config ?? {
        config: false,
        dks: false,
        mt: false,
        tgl: false,
        rs: false,
        socd: false,
        advanced: false,
      }
    )
  })

  const tabConfigs = computed(() => {
    // FN 层不支持 DKS
    let tabs =
      currentLayer.value === 'fn'
        ? allTabConfigs.value.filter((tab) => tab.value !== 'dks')
        : allTabConfigs.value
    // 根据设备能力隐藏不支持的 Tab
    if (!keyFeatures.value.config) tabs = tabs.filter((t) => t.value !== 'config')
    if (!keyFeatures.value.dks) tabs = tabs.filter((t) => t.value !== 'dks')
    if (!keyFeatures.value.mt) tabs = tabs.filter((t) => t.value !== 'mt')
    if (!keyFeatures.value.tgl) tabs = tabs.filter((t) => t.value !== 'tgl')
    if (!keyFeatures.value.rs) tabs = tabs.filter((t) => t.value !== 'rs')
    if (!keyFeatures.value.socd) tabs = tabs.filter((t) => t.value !== 'socd')
    if (!keyFeatures.value.advanced) tabs = tabs.filter((t) => t.value !== 'advanced')
    return tabs
  })

  const winLayer = computed<{ name: string; value: KeyLayer }[]>(() => [
    { name: t('keys.layers.winDefault'), value: 'default' },
    { name: t('keys.layers.winFn'), value: 'fn' },
  ])
  const macLayer = computed<{ name: string; value: KeyLayer }[]>(() => [
    { name: t('keys.layers.macDefault'), value: 'default' },
    { name: t('keys.layers.macFn'), value: 'fn' },
  ])

  const getLayer = () => {
    return currentPlatform.value === 'mac' ? macLayer.value : winLayer.value
  }

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const layoutKeys = computed(() => currentDevice.value?.manifest?.layout?.keys ?? [])

  /** 改键 v2 配置：出厂键表 + 多媒体白名单 + 展示改写规则 */
  const keysConfig = computed(() => {
    const cap = currentDevice.value?.manifest.capabilities?.find(
      (c): c is KeysCapability => c.type === 'keys',
    )
    return cap?.config
  })

  /** 当前 Win/Mac 层对应的 Fn 出厂键表 */
  const factoryKeys = computed(() =>
    pickFactoryKeyMap(keysConfig.value?.factoryKeys, currentPlatform.value),
  )

  /** 出厂定义仅在 Fn 层生效，默认层出厂即物理键自身 */
  const getFactoryKeywords = (keywords?: string): string | undefined => {
    if (currentLayer.value !== 'fn' || !keywords) return undefined
    return factoryKeys.value[keywords]
  }

  /** 网吧渠道：禁止将按键改成 Fn，常规键盘源也不展示 Fn */
  const isCybercafeChannel = computed(
    () => (import.meta.env.VITE_CHANNEL as string) === 'china_cybercafe',
  )

  const { isLoading, initLayerKeySettings, commitMappedKey, restoreMappedKey } = useMyKeysConf()

  const { macroList: repoMacroList, loadMacroList } = useMacroRepository()

  // 定义键盘数据
  const keySettings = ref<KeySetting[]>([])
  provide(KeySettingsSymbol, keySettings)

  /**
   * 用真实宏名称替换 keySettings 中的占位名称。
   */
  const resolveMacroNames = (settings: KeySetting[], macros: { index: number; name: string }[]) => {
    const nameMap = new Map(macros.map((m) => [m.index, m.name]))
    for (const setting of settings) {
      if (setting.keyType !== 'macro' || !setting.raw?.macro) continue
      const realName = nameMap.get(setting.raw.macro.index)
      if (realName) {
        setting.raw.macroName = realName
      }
    }
  }

  const getLayerKeySettings = async (isFn?: boolean) => {
    const fnLayer = isFn ?? currentLayer.value === 'fn'

    keySettings.value = await initLayerKeySettings({
      isFn: fnLayer,
      platform: currentPlatform.value,
      layoutKeys: layoutKeys.value,
      model: currentDevice.value?.manifest.model,
      factoryKeys: factoryKeys.value,
      displayOverrides: keysConfig.value?.displayOverrides,
      isFnLayer: fnLayer,
    })

    // 回显宏按键时，用 macroRepository 已加载的列表解析宏名称
    const hasMacroKeys = keySettings.value.some((s) => s.keyType === 'macro')
    if (hasMacroKeys) {
      // 如果宏列表尚未加载，先加载一次
      if (!repoMacroList.value.length) {
        await loadMacroList()
      }
      if (repoMacroList.value.length) {
        resolveMacroNames(keySettings.value, repoMacroList.value)
        keySettings.value = [...keySettings.value]
      }
    }
  }

  onMounted(async () => {
    await refreshKeyboardPlatform()
    await getLayerKeySettings()
    platformReady.value = true
  })

  provide(RefreshLayerKeySettingsSymbol, getLayerKeySettings)

  // 当前选中的按键
  const selectedKey = shallowRef<SelectedKey>(null)
  provide(SelectedKeySymbol, selectedKey)

  // 某些高级改键（RS/SOCD）在选键时只需要数据流，不需要键盘高亮/取消提示
  const suppressSelectedKeyUx = ref(false)
  provide(SuppressSelectedKeyUxSymbol, suppressSelectedKeyUx)

  // 右侧按键点击改键后需要触发弹窗的按键
  const rightSelectedKey = ref<string>()
  provide(RightSelectedKeySymbol, rightSelectedKey)

  // 当前激活的 tab
  const activeTabValue = ref<(typeof tabConfigs.value)[number]['value']>(tabConfigs.value[0].value)
  provide(ActiveTabValueSymbol, activeTabValue)

  const activeTabComponent = computed(() => {
    return tabConfigs.value.find((tab) => tab.value === activeTabValue.value)?.component
  })

  const upsertKeySetting = (targetKey: SelectedKey, setting: KeySetting) => {
    if (!targetKey?.keywords) return
    const next = [...keySettings.value]
    const index = findSettingIndexByTargetKey(next, targetKey)
    if (index >= 0) next[index] = setting
    else next.push(setting)
    keySettings.value = next
  }

  const removeKeySetting = (targetKey: SelectedKey, replacement?: KeySetting) => {
    if (!targetKey?.keywords) return
    const next = [...keySettings.value]
    const index = findSettingIndexByTargetKey(next, targetKey)
    if (index < 0) return
    if (replacement) next[index] = replacement
    else next.splice(index, 1)
    keySettings.value = next
  }

  /**
   * 设备写入串行化。RS/SOCD 会在同一轮里连发一对键，必须按顺序下发，
   * 但不能把乐观 UI 更新也排进去，否则成对键会分两次刷新、开关被 watch 拉回。
   */
  let keyMapWriteQueue: Promise<void> = Promise.resolve()
  const enqueueKeyMapWrite = async <T,>(task: () => Promise<T>): Promise<T> => {
    const next = keyMapWriteQueue.then(task)
    keyMapWriteQueue = next.then(
      () => undefined,
      () => undefined,
    )
    return next
  }

  const applyRestoredKeySetting = (targetKey: SelectedKey, factoryKeywords?: string, index = 0) => {
    if (!targetKey?.keywords) return
    if (factoryKeywords) {
      const factorySetting = buildFactoryDefaultSetting({
        physicalKeywords: targetKey.keywords,
        factoryKeywords,
        index,
      })
      if (factorySetting) upsertKeySetting(targetKey, factorySetting)
      else removeKeySetting(targetKey)
      return
    }
    removeKeySetting(targetKey)
  }

  // 更新选中按键的映射关系
  const updateSelectedKeyMapping = (data: KeyChangePayload) => {
    if (!selectedKey.value || !data) return

    updateKeyMappingWithData(data, selectedKey.value)
  }

  // 更新选中按键的映射关系 （RS/SOCD）
  const updateKeyMappingWithTarget = (data: KeyChangePayload, targetKey: SelectedKey) => {
    if (!targetKey || !data) return

    updateKeyMappingWithData(data, targetKey)
  }

  // 恢复按键，一般是无法在虚拟键盘通过tooltip恢复的按键，需要依赖注入手动触发
  const restoreKeyMappingWithTarget = async (targetKey: SelectedKey) => {
    if (!targetKey || isMacFnReadOnly.value) return

    const targetIndex = findSettingIndexByTargetKey(keySettings.value, targetKey)
    const targetSetting = targetIndex >= 0 ? keySettings.value[targetIndex] : undefined
    const isFn = currentLayer.value === 'fn'
    const factoryKeywords = getFactoryKeywords(targetKey.keywords)
    const previousSetting = targetSetting

    // 先改本地列表，成对恢复才能在同一次 tick 里两键一起消失
    if (targetIndex >= 0) {
      applyRestoredKeySetting(
        targetKey,
        factoryKeywords,
        parseNumber(targetSetting?.raw?.index) ?? parseNumber(targetKey.key) ?? 0,
      )
    }

    const payload = await enqueueKeyMapWrite(() =>
      restoreMappedKey({
        setting: targetSetting,
        defaultKey: targetKey,
        factoryKeywords,
        isFn,
        platform: currentPlatform.value,
      }),
    )

    if (!payload) {
      if (previousSetting) upsertKeySetting(targetKey, previousSetting)
      return
    }

    if (targetIndex >= 0 && factoryKeywords) {
      applyRestoredKeySetting(targetKey, factoryKeywords, payload.index ?? 0)
    }
  }

  // 提供更新键位映射的函数给子组件
  provide('updateSelectedKeyMapping', updateSelectedKeyMapping)
  provide(UpdateKeyMappingWithTargetSymbol, updateKeyMappingWithTarget)
  provide(RestoreKeyMappingWithTargetSymbol, restoreKeyMappingWithTarget)

  /**
   * 判断 raw 映射是否是 Fn 映射。
   *
   * @param raw keySetting/raw 或 payload/raw
   * @returns 是否映射为 Fn
   */
  const isFnMappingByRaw = (data: any): boolean => {
    return data?.target?.keywords === 'Fn' || data?.keywords === 'Fn'
  }

  /**
   * 判断改键 payload 是否是 Fn 映射。
   *
   * @param payload 改键数据
   * @returns 是否映射为 Fn
   */
  const isFnMappingPayload = (payload: KeyChangePayload): boolean => {
    return isFnMappingByRaw(payload)
  }

  /**
   * 解析目标键发送所需 index。
   * 优先级：existingSetting.raw.index -> targetKey.key（布局键 key 即横向 index）。
   *
   * @param targetKey 目标键
   * @param existingSetting 已存在的目标键设置
   * @returns index；解析失败时返回 undefined
   */
  const resolveTargetIndex = (targetKey: SelectedKey, existingSetting?: KeySetting) => {
    const settingIndex = parseNumber(existingSetting?.raw?.index)
    if (settingIndex != null) return settingIndex

    return parseNumber(targetKey?.key)
  }

  /**
   * 更新目标键映射并下发 SDK。
   *
   * @param sourceData 来源改键数据
   * @param targetKey 目标键
   * @returns 无返回值
   */
  const updateKeyMappingWithData = async (sourceData: KeyChangePayload, targetKey: SelectedKey) => {
    if (!targetKey || isMacFnReadOnly.value) return

    // 网吧版：禁止将任意键改成 Fn
    if (isCybercafeChannel.value && isFnMappingPayload(sourceData)) {
      toast.info(t('toast.keys.fnNotSupported'))
      return
    }

    const isFn = currentLayer.value === 'fn'
    const factoryResolved = resolveFactoryKey(getFactoryKeywords(targetKey.keywords))

    // 映射结果与出厂功能相同 → 视为回到出厂默认（恢复按钮对它没有意义）
    const sameAsFactory =
      factoryResolved &&
      isSameKeyIdentity(
        {
          type: sourceData.raw?.type,
          code: sourceData.raw?.code,
          confIndex: sourceData.raw?.index ?? sourceData.raw?.confIndex,
        },
        {
          type: factoryResolved.type,
          code: factoryResolved.code,
          confIndex: factoryResolved.confIndex,
        },
      )

    // 映射结果与物理键相同
    const samePhysicalKey = isSameKeyIdentity(
      {
        type: sourceData.raw?.type,
        code: sourceData.raw?.code,
        confIndex: sourceData.raw?.index ?? sourceData.raw?.confIndex,
      },
      {
        type: targetKey.type,
        code: targetKey.code,
        confIndex: (targetKey as any)?.confIndex,
      },
    )

    const currentSettings = [...keySettings.value]
    const existingIndex = findSettingIndexByTargetKey(currentSettings, targetKey)
    const existingSetting = existingIndex >= 0 ? currentSettings[existingIndex] : undefined

    if (sameAsFactory) {
      restoreKeyMappingWithTarget(targetKey)
      return
    }

    // 无出厂定义时，改回物理键 = 恢复；有出厂定义时改成物理键算一次真正改键
    if (samePhysicalKey && !factoryResolved) {
      if (existingSetting?.isModified) {
        restoreKeyMappingWithTarget(targetKey)
      }
      return
    }

    // RS/SOCD 这类高级功能键需要先关闭后再改到其他类型，避免配置冲突。
    // 允许 RS 与 SOCD 之间互改。
    if (
      (existingSetting?.keyType === 'RS' || existingSetting?.keyType === 'SOCD') &&
      sourceData.keyType !== 'RS' &&
      sourceData.keyType !== 'SOCD'
    ) {
      return toast.info(t('toast.keys.disableBeforeSwitch', { type: existingSetting.keyType }))
    }

    // 非网吧：Fn 映射限制最多 4 个
    if (!isCybercafeChannel.value && isFnMappingPayload(sourceData)) {
      const fnMappedCount = currentSettings.filter((setting) => isFnMappingByRaw(setting)).length
      const targetWasFnMapped =
        existingIndex >= 0 && isFnMappingByRaw(currentSettings[existingIndex])

      if (!targetWasFnMapped && fnMappedCount >= 4) {
        toast.info(t('toast.keys.maxFnMappings'))
        return
      }
    }

    const targetIndex = resolveTargetIndex(targetKey, existingSetting)
    if (targetIndex == null) return

    const newSetting = buildKeySettingFromPayload({
      sourceData,
      index: targetIndex,
    })
    if (!newSetting) return

    // 走到这里说明映射结果与出厂不同（相同的已在 sameAsFactory 分支返回）
    if (factoryResolved) {
      newSetting.isFactoryDefault = false
    }

    // 先写本地列表再等 HID。RS/SOCD 同一轮连发两键时，两份乐观更新会进同一次 flush，
    // 避免后一次用过期快照把前一键盖掉，也避免成对开关被 watch 拉回。
    upsertKeySetting(targetKey, newSetting)
    selectedKey.value = null
    rightSelectedKey.value = targetKey?.keywords

    const committed = await enqueueKeyMapWrite(() =>
      commitMappedKey({
        setting: newSetting,
        existingSetting,
        isFn,
        platform: currentPlatform.value,
      }),
    )
    if (!committed) {
      if (existingSetting) upsertKeySetting(targetKey, existingSetting)
      else removeKeySetting(targetKey)
      return
    }

    if (newSetting.target?.keywords === 'Fn' && !isFn) {
      await enqueueKeyMapWrite(() =>
        commitMappedKey({
          setting: newSetting,
          existingSetting,
          isFn: true,
          platform: currentPlatform.value,
        }),
      )
    }
  }

  const configuration = useConfigurationStore()
  const { currentConfig } = storeToRefs(configuration)

  /**
   * 切换键盘层，并加载该层预设改键。
   */
  const handleChangeLayer = async (key: KeyLayer) => {
    currentLayer.value = key
    const isFn = key === 'fn'

    if (isFn && activeTabValue.value === 'dks') {
      activeTabValue.value = allTabConfigs.value[0].value
    }

    // 切换需要清空当前按键配置显示
    keySettings.value = []
    // 重新获取按键配置
    await getLayerKeySettings(isFn)
  }

  // 监听配置切换重新获取按键配置（上报 CfgChanged → currentConfig 更新）
  // 2s 等待固件 settle；多次切换时合并实际回读，避免同地址连读 busy
  const cfgKeyFetchQueue = createCoalesceLatestRequestQueue('keys.cfgReport')
  watch(
    () => currentConfig.value?.index,
    async () => {
      await new Promise((resolve) => setTimeout(resolve, 2000))
      cfgKeyFetchQueue.enqueue(() => handleChangeLayer(currentLayer.value))
    },
  )

  // 硬件 Win/Mac 切换后重新拉取当前层改键（初始 refresh 不走这条路径，避免重复读）
  watch(currentPlatform, async () => {
    if (!platformReady.value) return
    selectedKey.value = null
    keySettings.value = []
    await getLayerKeySettings()
  })

  /**
   * 选中按键后的提示文案：
   * - 宏 tab：使用宏专属提示（macroBindSelectMacro）
   * - 其他 tab：使用通用提示（deselectKey），id 为 'keyboard'
   *
   * 切换 tab 时，如果当前已有选中按键，需要在两种提示之间无缝切换。
   */
  const KEYBOARD_TOAST_ID = 'keyboard'
  const MACRO_TOAST_ID = 'macro-bind-select-macro'

  const showGenericSelectionToast = () => {
    toast.dismiss(MACRO_TOAST_ID)
    toast(t('toast.deselectKey'), {
      id: KEYBOARD_TOAST_ID,
      duration: Infinity,
      class: 'text-state-success',
    })
  }

  const showMacroSelectionToast = () => {
    toast.dismiss(KEYBOARD_TOAST_ID)
    toast.info(t('toast.keys.macroBindSelectMacro'), {
      id: MACRO_TOAST_ID,
      duration: Infinity,
    })
  }

  const dismissSelectionToasts = () => {
    toast.dismiss(KEYBOARD_TOAST_ID)
    toast.dismiss(MACRO_TOAST_ID)
  }

  // 切换 tab：若已有选中按键，切换两种提示之间的归属
  watch(activeTabValue, (next, prev) => {
    if (!selectedKey.value) return
    // 离开宏 tab → 切到通用提示
    if (prev === 'macro' && next !== 'macro') {
      showGenericSelectionToast()
      return
    }
    // 进入宏 tab → 切到宏提示（由 TabMacro 内部处理待绑定流程时的提示，这里仅做兜底切换）
    if (next === 'macro' && prev !== 'macro') {
      showMacroSelectionToast()
    }
  })

  // 路由离开时清理选中状态及提示
  onUnmounted(() => {
    selectedKey.value = null
    rightSelectedKey.value = undefined
    dismissSelectionToasts()
  })
</script>

<style></style>
